/**
 * 提醒偏好登记表（来电铃声开关 / 提醒音量）——与 `theme/borders.ts`、`theme/uiPrefs.ts` 同一模式：
 * 本文件只存"参数"，读取与持久化都在本文件，下发点是用它的 `utils/ringAlert.ts`。
 *
 * 为什么不放进 `theme/`：那两个文件的下发点是 `applyTheme`（写 CSS 变量），而这两项作用于音频。
 * `utils/listPrefs.ts`（按路由记每页条数）是同类先例——非视觉偏好落在 `utils/` 自负读写。
 *
 * 为什么要做成可配：
 * - **来电铃声**：防漏接靠的是"一定听得见"，但同一套音量无法兼顾戴耳机与不戴耳机两种状态；
 *   而不给开关，用户唯一的替代手段是调浏览器/系统音量——那会**连带把 WebRTC 通话音一起调小或静音**，
 *   所以"只关铃声、不关通话"这件事只能在应用内做。
 * - **提醒音量**：作用于所有合成提示音（来电振铃 / 高风险上报 / 排队提醒），三处共用同一个蜂鸣，
 *   音量分两套只会让用户困惑；它是"音量"不是"开关"，故不承担"关掉提醒"的职责。
 *
 * 关闭铃声**只停振铃循环**：标签页标题闪烁与系统通知照旧——那是页面不在前台时的兜底提醒，
 * 与本开关的"我不想听声音"是两件事，一起关掉会直接造成漏接。
 *
 * 各档默认值等于"改动前的既有观感"：铃声响、音量标准（gain 0.25，即原先硬编码的那一档）。
 */

export type RingEnabledLevelKey = 'on' | 'off';
export type RingVolumeLevelKey = 'soft' | 'standard' | 'loud';

export const RING_ENABLED_STORAGE_KEY = 'bcc.ring.enabled';
export const RING_VOLUME_STORAGE_KEY = 'bcc.ring.volume';

export const DEFAULT_RING_ENABLED: RingEnabledLevelKey = 'on';
export const DEFAULT_RING_VOLUME: RingVolumeLevelKey = 'standard';

export interface RingEnabledLevelDef {
  key: RingEnabledLevelKey;
  name: string;
  desc: string;
}

export interface RingVolumeLevelDef {
  key: RingVolumeLevelKey;
  name: string;
  desc: string;
  /** 合成提示音的音量包络峰值（0~1）；`standard` 即原先 `beepOnce` 的硬编码值 */
  gain: number;
}

export const RING_ENABLED_LEVELS: RingEnabledLevelDef[] = [
  { key: 'on', name: '开启', desc: '来电时循环振铃；页面不在前台仍有标题闪烁与系统通知' },
  { key: 'off', name: '关闭', desc: '不发出振铃音，标题闪烁与系统通知照旧（防漏接兜底保留）' },
];

export const RING_VOLUME_LEVELS: RingVolumeLevelDef[] = [
  { key: 'soft', name: '轻', desc: '音量约为标准的一半，适合安静工位或外放', gain: 0.12 },
  { key: 'standard', name: '标准', desc: '与其他提示音一致的默认音量', gain: 0.25 },
  { key: 'loud', name: '响', desc: '适合未戴耳机、环境嘈杂的坐席', gain: 0.5 },
];

/** 按 key 取档位（未知 key 回退默认档；不要写成固定下标，否则改默认值时存储空值会落到另一档） */
function pick<T extends { key: string }>(list: T[], key: string | null | undefined, fallback: string): T {
  return list.find((x) => x.key === key) ?? list.find((x) => x.key === fallback) ?? list[0];
}

export const getRingEnabledLevel = (key: string | null | undefined) =>
  pick(RING_ENABLED_LEVELS, key, DEFAULT_RING_ENABLED);
export const getRingVolumeLevel = (key: string | null | undefined) =>
  pick(RING_VOLUME_LEVELS, key, DEFAULT_RING_VOLUME);

function readPref(storageKey: string): string | null {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function writePref(storageKey: string, value: string): void {
  try {
    localStorage.setItem(storageKey, value);
  } catch {
    /* 隐私模式等存储不可用时忽略：本次会话内仍生效 */
  }
}

export function readStoredRingEnabled(): string {
  return getRingEnabledLevel(readPref(RING_ENABLED_STORAGE_KEY)).key;
}

export function readStoredRingVolume(): string {
  return getRingVolumeLevel(readPref(RING_VOLUME_STORAGE_KEY)).key;
}

export function setRingEnabledLevel(level: string): void {
  writePref(RING_ENABLED_STORAGE_KEY, getRingEnabledLevel(level).key);
}

export function setRingVolumeLevel(level: string): void {
  writePref(RING_VOLUME_STORAGE_KEY, getRingVolumeLevel(level).key);
}

/** 当前生效的振铃开关（`ringAlert` 在每次振铃前读取，故改设置即时生效、无需刷新） */
export const ringEnabled = (): boolean => readStoredRingEnabled() === 'on';

/** 当前生效的提示音音量峰值 */
export const ringGain = (): number => getRingVolumeLevel(readStoredRingVolume()).gain;
